import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShoppingBag, MapPin, Truck, Store, User, Phone,
  DollarSign, CreditCard, QrCode, Tag, Check, AlertCircle,
  Plus, Minus, Sparkles, ShieldCheck, LogIn, CheckCircle2,
  ChevronDown, ChevronUp, Search, Coins
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../hooks/useCart';
import useFrete, { Endereco } from '../hooks/useFrete';
import { usePromocoes } from '../hooks/usePromocoes';
import MercadoPagoCheckout from '../components/MercadoPagoCheckout';
import api from '../services/api';
import toast from 'react-hot-toast';

export const CheckoutPage: React.FC = () => {
  const { empresaId: rawEmpresaId } = useParams<{ empresaId?: string }>();
  const navigate = useNavigate();
  const empresaId = useMemo(() => {
    return rawEmpresaId && /^\d+$/.test(rawEmpresaId) ? parseInt(rawEmpresaId) : 1;
  }, [rawEmpresaId]);

  const { usuario, login } = useAuth();
  const cart = useCart(empresaId);
  const promocoes = usePromocoes(empresaId);

  // ── ESTADOS DE ENTREGA ──────────────────────────────────────────────
  const [tipoEntrega, setTipoEntrega] = useState<'entrega' | 'retirada'>('entrega');
  const [endereco, setEndereco] = useState<Endereco>({
    rua: '',
    numero_casa: '',
    bairro: '',
    cidade: 'Florianópolis',
    estado: 'SC',
    cep: ''
  });
  const [complemento, setComplemento] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [enderecoPredefinidoId, setEnderecoPredefinidoId] = useState<number | null>(null);

  // Hook de Frete
  const { calcularFrete, resultadoFrete, loading: freteLoading, error: freteError } = useFrete({ empresaId });

  // ── ESTADOS DE IDENTIFICAÇÃO (GUEST / USUÁRIO) ──────────────────────
  const [nomeCliente, setNomeCliente] = useState('');
  const [whatsappCliente, setWhatsappCliente] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginSenha, setLoginSenha] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // ── ESTADOS DE PAGAMENTO ────────────────────────────────────────────
  // 'dinheiro' | 'cartao_maquininha' | 'pix_maquininha' | 'pix_online'
  const [formaPagamento, setFormaPagamento] = useState<string>('dinheiro');
  const [precisaTroco, setPrecisaTroco] = useState(false);
  const [valorTrocoPara, setValorTrocoPara] = useState<string>('');

  // ── ESTADOS DE CUPOM E OBSERVAÇÕES ──────────────────────────────────
  const [codigoCupom, setCodigoCupom] = useState('');
  const [cupomAplicado, setCupomAplicado] = useState<{ codigo: string; promocao: any; desconto: number } | null>(null);
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [observacoesPedido, setObservacoesPedido] = useState('');

  // ── CONTROLE MOBILE: ACCORDION RESUMO ────────────────────────────────
  const [showResumoMobile, setShowResumoMobile] = useState(false);

  // ── ESTADOS DE CONCLUSÃO E MERCADO PAGO ─────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [pedidoCriado, setPedidoCriado] = useState<any>(null);
  const [showMercadoPago, setShowMercadoPago] = useState(false);
  const ultimoEnderecoFreteRef = React.useRef<string>('');

  // Sincronizar dados do usuário autenticado
  useEffect(() => {
    if (usuario) {
      if (usuario.nome && !nomeCliente) setNomeCliente(usuario.nome);
      if (usuario.telefone && !whatsappCliente) setWhatsappCliente(usuario.telefone);
      if (usuario.email && !emailCliente) setEmailCliente(usuario.email);

      if (usuario.enderecos && usuario.enderecos.length > 0 && !endereco.rua) {
        const principal = usuario.enderecos[0];
        setEnderecoPredefinidoId(principal.id);
        const end: Endereco = {
          id: principal.id,
          rua: principal.rua || '',
          numero_casa: principal.numero_casa || '',
          bairro: principal.bairro || '',
          cidade: principal.cidade || 'Florianópolis',
          estado: principal.estado || 'SC',
          cep: principal.cep || ''
        };
        setEndereco(end);
        const endKey = `${end.rua}_${end.numero_casa}_${end.cep}_${empresaId}`;
        if (ultimoEnderecoFreteRef.current !== endKey) {
          ultimoEnderecoFreteRef.current = endKey;
          calcularFrete(end, empresaId);
        }
      } else if (usuario.rua && !endereco.rua) {
        const end: Endereco = {
          rua: usuario.rua || '',
          numero_casa: usuario.numero_casa || '',
          bairro: usuario.bairro || '',
          cidade: usuario.cidade || 'Florianópolis',
          estado: usuario.estado || 'SC',
          cep: usuario.cep || ''
        };
        setEndereco(end);
        const endKey = `${end.rua}_${end.numero_casa}_${end.cep}_${empresaId}`;
        if (ultimoEnderecoFreteRef.current !== endKey) {
          ultimoEnderecoFreteRef.current = endKey;
          calcularFrete(end, empresaId);
        }
      }
    }
  }, [usuario?.id, empresaId]);

  // Formatação de Telefone
  const formatarTelefone = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 7) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
  };

  // Formatação de CEP
  const formatarCEP = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 5) return numeros;
    return `${numeros.slice(0, 5)}-${numeros.slice(5, 8)}`;
  };

  // Consulta de CEP via ViaCEP
  const buscarCEP = async (cepValue: string) => {
    const cepLimpo = cepValue.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();
      if (!data.erro) {
        const endAtualizado: Endereco = {
          ...endereco,
          rua: data.logradouro || endereco.rua,
          bairro: data.bairro || endereco.bairro,
          cidade: data.localidade || endereco.cidade,
          estado: data.uf || endereco.estado,
          cep: formatarCEP(cepLimpo)
        };
        setEndereco(endAtualizado);
        toast.success('Endereço localizado com sucesso!');
        calcularFrete(endAtualizado, empresaId);
      } else {
        toast.error('CEP não encontrado');
      }
    } catch {
      toast.error('Erro ao consultar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  // Cálculos de Totais
  const subtotalItens = useMemo(() => {
    return cart.cartData.total || 0;
  }, [cart.cartData.total]);

  const temDireitoFreteGratis = useMemo(() => {
    if (tipoEntrega === 'retirada') return true;
    const promocional = promocoes.temDireitoFreteGratis ? promocoes.temDireitoFreteGratis(subtotalItens) : false;
    return promocional || cart.cartData.entregaGratis;
  }, [tipoEntrega, promocoes, subtotalItens, cart.cartData.entregaGratis]);

  const valorFreteFinal = useMemo(() => {
    if (tipoEntrega === 'retirada') return 0;
    if (temDireitoFreteGratis) return 0;
    return resultadoFrete?.valorFrete || 0;
  }, [tipoEntrega, temDireitoFreteGratis, resultadoFrete]);

  const valorDescontoCupom = useMemo(() => {
    if (!cupomAplicado) return 0;
    return cupomAplicado.desconto || 0;
  }, [cupomAplicado]);

  const valorTotalFinal = useMemo(() => {
    const total = subtotalItens + valorFreteFinal - valorDescontoCupom;
    return total > 0 ? total : 0;
  }, [subtotalItens, valorFreteFinal, valorDescontoCupom]);

  // Troco calculado
  const trocoCalculado = useMemo(() => {
    if (!precisaTroco || !valorTrocoPara) return 0;
    const num = parseFloat(valorTrocoPara.replace(',', '.'));
    if (isNaN(num) || num <= valorTotalFinal) return 0;
    return num - valorTotalFinal;
  }, [precisaTroco, valorTrocoPara, valorTotalFinal]);

  // Aplicar Cupom
  const handleAplicarCupom = async () => {
    if (!codigoCupom.trim()) return;
    setValidandoCupom(true);
    try {
      if (promocoes.validarCupom) {
        const res = await promocoes.validarCupom(codigoCupom.trim(), subtotalItens);
        if (res.valido && res.promocao) {
          const desconto = promocoes.calcularDescontoCupom 
            ? promocoes.calcularDescontoCupom(subtotalItens, res.promocao) 
            : 0;
          setCupomAplicado({
            codigo: codigoCupom.trim().toUpperCase(),
            promocao: res.promocao,
            desconto
          });
          toast.success(`Cupom ${codigoCupom.toUpperCase()} aplicado!`);
        } else {
          toast.error(res.mensagem || 'Cupom inválido ou expirado');
        }
      } else {
        toast.error('Sistema de cupons indisponível no momento');
      }
    } catch {
      toast.error('Erro ao validar cupom');
    } finally {
      setValidandoCupom(false);
    }
  };

  // Login Rápido
  const handleLoginRapido = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginSenha) {
      toast.error('Preencha email e senha');
      return;
    }

    setLoginLoading(true);
    try {
      await login(loginEmail, loginSenha);
      setShowLoginModal(false);
      toast.success('Login realizado! Seus endereços foram carregados.');
    } catch (err: any) {
      toast.error(err.message || 'Credenciais inválidas');
    } finally {
      setLoginLoading(false);
    }
  };

  // Finalização do Pedido
  const handleFinalizarCompra = async () => {
    if (!cart.hasItems) {
      toast.error('Seu carrinho está vazio');
      navigate(`/menu/${empresaId}`);
      return;
    }

    const nomeFinal = nomeCliente.trim();
    if (!nomeFinal) {
      toast.error('Por favor, informe seu nome');
      return;
    }

    const whatsappLimpo = whatsappCliente.replace(/\D/g, '');
    if (whatsappLimpo.length < 10) {
      toast.error('Informe um WhatsApp válido com DDD');
      return;
    }

    if (tipoEntrega === 'entrega') {
      if (!endereco.rua || !endereco.numero_casa || !endereco.bairro) {
        toast.error('Preencha a Rua, Número e Bairro para entrega');
        return;
      }
    }

    let valorTrocoNum = 0;
    if (formaPagamento === 'dinheiro' && precisaTroco) {
      valorTrocoNum = parseFloat(valorTrocoPara.replace(',', '.'));
      if (isNaN(valorTrocoNum) || valorTrocoNum < valorTotalFinal) {
        toast.error(`O valor para troco deve ser maior que R$ ${valorTotalFinal.toFixed(2)}`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const itensFormatados = cart.cartData.itens.map(item => ({
        produto_id: item.produto_id,
        quantidade: item.quantidade,
        preco_unitario: item.preco_unitario,
        observacoes: item.observacoes || '',
        personalizacoes: (item.personalizacoes || []).map(p => ({
          opcao_id: p.opcao_id,
          item_id: p.item_id,
          nome: p.nome || '',
          tipo: 'adicionar',
          quantidade: p.quantidade || 1,
          preco_adicional: p.preco_adicional || 0
        }))
      }));

      let metodoApi = 'entrega';
      let detalhesPagamento: any = {};

      if (formaPagamento === 'dinheiro') {
        detalhesPagamento = {
          metodo: 'dinheiro',
          precisaTroco,
          valorTroco: precisaTroco ? valorTrocoNum : 0,
          trocoCalculado
        };
      } else if (formaPagamento === 'cartao_maquininha') {
        detalhesPagamento = { metodo: 'cartao', tipo: 'maquininha' };
      } else if (formaPagamento === 'pix_maquininha') {
        detalhesPagamento = { metodo: 'pix', tipo: 'entrega' };
      } else if (formaPagamento === 'pix_online') {
        metodoApi = 'site';
        detalhesPagamento = { metodo: 'mercadopago' };
      }

      const dadosPedido = {
        empresa_id: empresaId,
        mesa: tipoEntrega === 'retirada' ? 'Retirada' : 'Entrega',
        tipo_pedido: tipoEntrega,
        itens: itensFormatados,
        observacoes: observacoesPedido || '',
        dados_cliente: {
          nome: nomeFinal.split(' ')[0],
          sobrenome: nomeFinal.split(' ').slice(1).join(' ') || '',
          telefone: whatsappCliente,
          email: emailCliente || `${whatsappLimpo}@delivery.com`,
          endereco: tipoEntrega === 'entrega' ? { ...endereco, complemento } : { retirada: true }
        },
        valor_frete: valorFreteFinal,
        metodo_pagamento: metodoApi,
        detalhes_pagamento: detalhesPagamento,
        cupom_codigo: cupomAplicado?.codigo || null,
        cupom_id: cupomAplicado?.promocao?.id || null,
        desconto_cupom: valorDescontoCupom
      };

      if (formaPagamento === 'pix_online') {
        setPedidoCriado(dadosPedido);
        setShowMercadoPago(true);
        setSubmitting(false);
        return;
      }

      const res = await api.post('/api/pedidos', dadosPedido);

      toast.success('🎉 Pedido realizado com sucesso!', { duration: 3000 });
      await cart.clearCart();

      setTimeout(() => {
        navigate(`/menu/${empresaId}?pedidoConcluido=${res.data.id || 'ok'}`);
      }, 1500);

    } catch (err: any) {
      console.error('Erro ao finalizar pedido:', err);
      const msg = err.response?.data?.error || err.response?.data?.erro || 'Erro ao processar seu pedido';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMercadoPagoConfirmado = async (status: string) => {
    if (status === 'approved') {
      await cart.clearCart();
      setShowMercadoPago(false);
      toast.success('🎉 Pagamento PIX aprovado! Seu pedido já está sendo preparado.');
      setTimeout(() => {
        navigate(`/menu/${empresaId}?pedidoConcluido=pix`);
      }, 1500);
    }
  };

  // Se o carrinho estiver vazio
  if (!cart.isLoading && !cart.hasItems) {
    return (
      <div className="min-h-screen bg-[#0a0f1c] text-white flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 mb-4 animate-pulse">
          <ShoppingBag className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-black mb-2">Seu carrinho está vazio</h2>
        <p className="text-gray-400 max-w-sm mb-6 text-sm">
          Adicione lanches e itens saborosos ao carrinho para avançar para a compra.
        </p>
        <button
          onClick={() => navigate(`/menu/${empresaId}`)}
          className="bg-gradient-to-r from-orange-500 to-amber-500 hover:brightness-110 text-white font-bold px-8 py-3.5 rounded-2xl flex items-center gap-2 shadow-lg shadow-orange-500/20 active:scale-95 transition-all text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao Cardápio
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white selection:bg-orange-500 selection:text-white pb-36 lg:pb-16 font-sans">
      
      {/* ── TOPBAR FIXA COM PROTEÇÃO DE ÁREA ───────────────────────── */}
      <header className="sticky top-0 z-40 bg-[#0d1424]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3.5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(`/menu/${empresaId}`)}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors p-1 rounded-xl hover:bg-white/5 active:scale-95"
            aria-label="Voltar"
          >
            <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
              <ArrowLeft className="w-4 h-4" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider hidden sm:inline">Cardápio</span>
          </button>

          <div className="text-center">
            <h1 className="text-sm sm:text-base font-black tracking-tight text-white">
              Finalizar Pedido
            </h1>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest hidden sm:block">
              Etapa de Entrega e Pagamento
            </p>
          </div>

          <div className="flex items-center gap-1.5 text-emerald-400 text-[11px] font-bold bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Seguro</span>
          </div>
        </div>
      </header>

      {/* ── ACCORDION RESUMO NO MOBILE (TOPO) ───────────────────────── */}
      <div className="lg:hidden max-w-5xl mx-auto px-4 pt-3">
        <button
          type="button"
          onClick={() => setShowResumoMobile(!showResumoMobile)}
          className="w-full bg-[#111827] border border-white/10 rounded-2xl p-3 flex items-center justify-between shadow-md active:bg-[#151c2c] transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400">
              <ShoppingBag className="w-3.5 h-3.5" />
            </div>
            <div className="text-left">
              <span className="text-xs font-bold text-white block">
                {cart.itemCount} {cart.itemCount === 1 ? 'item' : 'itens'} no pedido
              </span>
              <span className="text-[11px] text-emerald-400 font-semibold">
                Total: R$ {valorTotalFinal.toFixed(2)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-orange-400 font-bold">
            <span>{showResumoMobile ? 'Ocultar' : 'Ver itens'}</span>
            {showResumoMobile ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {showResumoMobile && (
          <div className="mt-2 bg-[#111827] border border-white/10 rounded-2xl p-3.5 space-y-2.5 shadow-xl animate-fade-in">
            {cart.cartData.itens.map(item => (
              <div key={item.id} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
                <div className="flex-1 pr-2">
                  <p className="font-bold text-white">{item.quantidade}x {item.produto_nome}</p>
                  {item.personalizacoes?.length > 0 && (
                    <p className="text-[10px] text-gray-400 truncate">
                      {item.personalizacoes.map((p: any) => p.nome).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-orange-400">
                    R$ {(item.preco_unitario * item.quantidade).toFixed(2)}
                  </span>
                  <div className="flex items-center gap-1 bg-[#151c2c] rounded-lg p-0.5 border border-white/10">
                    <button
                      onClick={() => cart.updateItemQuantity(item.id, item.quantidade - 1)}
                      className="w-5 h-5 flex items-center justify-center text-gray-300"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => cart.updateItemQuantity(item.id, item.quantidade + 1)}
                      className="w-5 h-5 flex items-center justify-center text-gray-300"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── CONTEÚDO PRINCIPAL ─────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-4 pt-4 sm:pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 items-start">
          
          {/* ════════════ COLUNA ESQUERDA: ETAPAS DE COMPRA ════════════ */}
          <div className="lg:col-span-7 space-y-4 sm:space-y-5">

            {/* CARD 1: IDENTIFICAÇÃO DO CLIENTE */}
            <section className="bg-[#111827] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg shadow-black/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 font-black text-xs">
                    1
                  </div>
                  <div>
                    <h2 className="text-sm sm:text-base font-bold text-white">Seus Dados</h2>
                    <p className="text-[11px] text-gray-400">Para envio do pedido e atualizações</p>
                  </div>
                </div>

                {!usuario && (
                  <button
                    type="button"
                    onClick={() => setShowLoginModal(true)}
                    className="text-xs font-bold text-orange-400 hover:text-orange-300 bg-orange-500/10 border border-orange-500/20 px-2.5 py-1.5 rounded-xl flex items-center gap-1 active:scale-95 transition-all"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    <span>Entrar</span>
                  </button>
                )}
              </div>

              {usuario ? (
                <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-orange-500 to-amber-500 flex items-center justify-center font-bold text-xs text-white">
                      {usuario.nome ? usuario.nome[0].toUpperCase() : 'U'}
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-bold text-white flex items-center gap-1">
                        {usuario.nome}
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      </p>
                      <p className="text-[11px] text-gray-400">{usuario.telefone || usuario.email}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-md uppercase">
                    Logado
                  </span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1">
                      Seu Nome Completo *
                    </label>
                    <div className="relative">
                      <User className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Ex: João da Silva"
                        value={nomeCliente}
                        onChange={(e) => setNomeCliente(e.target.value)}
                        className="w-full bg-[#151c2c] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1">
                      WhatsApp com DDD *
                    </label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="tel"
                        placeholder="(00) 00000-0000"
                        value={whatsappCliente}
                        onChange={(e) => setWhatsappCliente(formatarTelefone(e.target.value))}
                        className="w-full bg-[#151c2c] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* CARD 2: TIPO DE ENTREGA & ENDEREÇO */}
            <section className="bg-[#111827] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg shadow-black/30">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 font-black text-xs">
                  2
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-bold text-white">Recebimento</h2>
                  <p className="text-[11px] text-gray-400">Escolha entrega ou retirar no balcão</p>
                </div>
              </div>

              {/* TABS DE SELEÇÃO: ENTREGA VS BALCÃO */}
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                <button
                  type="button"
                  onClick={() => setTipoEntrega('entrega')}
                  className={`p-3 sm:p-3.5 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all active:scale-95 ${
                    tipoEntrega === 'entrega'
                      ? 'bg-orange-500/15 border-orange-500 text-white shadow-md shadow-orange-500/10'
                      : 'bg-[#151c2c]/70 border-white/5 text-gray-400 hover:border-white/15'
                  }`}
                >
                  <Truck className={`w-5 h-5 ${tipoEntrega === 'entrega' ? 'text-orange-400' : 'text-gray-400'}`} />
                  <span className="text-xs font-bold uppercase tracking-wider">Entrega</span>
                  <span className={`text-[10px] font-semibold ${temDireitoFreteGratis ? 'text-emerald-400' : 'text-gray-400'}`}>
                    {temDireitoFreteGratis ? '🎉 Frete Grátis' : 'Taxa por KM'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setTipoEntrega('retirada')}
                  className={`p-3 sm:p-3.5 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all active:scale-95 ${
                    tipoEntrega === 'retirada'
                      ? 'bg-orange-500/15 border-orange-500 text-white shadow-md shadow-orange-500/10'
                      : 'bg-[#151c2c]/70 border-white/5 text-gray-400 hover:border-white/15'
                  }`}
                >
                  <Store className={`w-5 h-5 ${tipoEntrega === 'retirada' ? 'text-orange-400' : 'text-gray-400'}`} />
                  <span className="text-xs font-bold uppercase tracking-wider">Retirada</span>
                  <span className="text-[10px] text-emerald-400 font-semibold">Sem taxa</span>
                </button>
              </div>

              {tipoEntrega === 'retirada' ? (
                <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3">
                  <Store className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-white mb-0.5">Retirada no Balcão</p>
                    <p className="text-[11px] text-gray-300">
                      Você retira seu pedido diretamente na loja assim que estiver pronto. Avisaremos no seu WhatsApp.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {/* Se o usuário tiver endereços salvos */}
                  {usuario?.enderecos && usuario.enderecos.length > 0 && (
                    <div className="mb-3">
                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Endereços Cadastrados
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {usuario.enderecos.map(end => (
                          <div
                            key={end.id}
                            onClick={() => {
                              setEnderecoPredefinidoId(end.id);
                              const novoEnd: Endereco = {
                                id: end.id,
                                rua: end.rua,
                                numero_casa: end.numero_casa,
                                bairro: end.bairro,
                                cidade: end.cidade,
                                estado: end.estado || 'SC',
                                cep: end.cep
                              };
                              setEndereco(novoEnd);
                              calcularFrete(novoEnd, empresaId);
                            }}
                            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
                              enderecoPredefinidoId === end.id
                                ? 'bg-orange-500/15 border-orange-500 text-white'
                                : 'bg-[#151c2c] border-white/5 text-gray-300 hover:border-white/15'
                            }`}
                          >
                            <p className="text-xs font-bold text-white flex items-center justify-between">
                              {end.apelido || 'Endereço'}
                              {enderecoPredefinidoId === end.id && <Check className="w-3.5 h-3.5 text-orange-400" />}
                            </p>
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">
                              {end.rua}, {end.numero_casa} - {end.bairro}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* FORMULÁRIO RESPONSIVO DE ENDEREÇO (MOBILE-FIRST) */}
                  <div className="space-y-3.5 pt-1">
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1.5">
                        CEP (busca automática de endereço)
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <MapPin className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="00000-000"
                            value={endereco.cep}
                            onChange={(e) => {
                              const val = formatarCEP(e.target.value);
                              setEndereco({ ...endereco, cep: val });
                              if (val.replace(/\D/g, '').length === 8) {
                                buscarCEP(val);
                              }
                            }}
                            className="w-full bg-[#151c2c] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => buscarCEP(endereco.cep || '')}
                          disabled={cepLoading || (endereco.cep?.replace(/\D/g, '').length !== 8)}
                          className="bg-[#1e2738] hover:bg-[#253248] disabled:opacity-40 text-gray-200 font-bold px-4 py-3 rounded-xl text-xs flex items-center gap-1.5 transition-colors shrink-0 active:scale-95"
                        >
                          {cepLoading ? (
                            <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                          <span>Buscar</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1.5">
                        Rua / Avenida *
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Rua das Flores"
                        value={endereco.rua}
                        onChange={(e) => setEndereco({ ...endereco, rua: e.target.value })}
                        className="w-full bg-[#151c2c] border border-white/10 rounded-xl px-3.5 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-12 gap-2.5">
                      <div className="col-span-4 sm:col-span-3">
                        <label className="block text-xs font-semibold text-gray-300 mb-1.5 truncate">
                          Número *
                        </label>
                        <input
                          type="text"
                          placeholder="123"
                          value={endereco.numero_casa}
                          onChange={(e) => {
                            const novo = { ...endereco, numero_casa: e.target.value };
                            setEndereco(novo);
                            if (endereco.rua && endereco.bairro) calcularFrete(novo, empresaId);
                          }}
                          className="w-full bg-[#151c2c] border border-white/10 rounded-xl px-3 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all text-center sm:text-left"
                        />
                      </div>

                      <div className="col-span-8 sm:col-span-9">
                        <label className="block text-xs font-semibold text-gray-300 mb-1.5 truncate">
                          Bairro *
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Centro"
                          value={endereco.bairro}
                          onChange={(e) => {
                            const novo = { ...endereco, bairro: e.target.value };
                            setEndereco(novo);
                            if (endereco.rua) calcularFrete(novo, empresaId);
                          }}
                          className="w-full bg-[#151c2c] border border-white/10 rounded-xl px-3.5 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1.5">
                        Complemento / Referência (opcional)
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Apto 101, Portão preto, Casa dos fundos..."
                        value={complemento}
                        onChange={(e) => setComplemento(e.target.value)}
                        className="w-full bg-[#151c2c] border border-white/10 rounded-xl px-3.5 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all"
                      />
                    </div>

                    {/* FEEDBACK DO FRETE */}
                    {freteLoading ? (
                      <div className="flex items-center gap-2.5 text-xs text-orange-400 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
                        <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full shrink-0" />
                        <span className="font-semibold">Calculando taxa de entrega em tempo real...</span>
                      </div>
                    ) : resultadoFrete ? (
                      <div className="flex items-center justify-between text-xs p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex items-center gap-2.5">
                          <Truck className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span className="text-gray-200">
                            {resultadoFrete.distancia ? `Distância estimada: ~${resultadoFrete.distancia.toFixed(1)} km` : 'Entrega disponível na sua região'}
                          </span>
                        </div>
                        <span className="font-black text-emerald-400 text-sm shrink-0">
                          {temDireitoFreteGratis ? '🎉 Grátis' : `R$ ${resultadoFrete.valorFrete.toFixed(2)}`}
                        </span>
                      </div>
                    ) : freteError ? (
                      <div className="flex items-center gap-2 text-xs text-amber-400 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{freteError}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </section>

            {/* CARD 3: FORMA DE PAGAMENTO (MOBILE-FIRST SEM SOBREPOSIÇÃO) */}
            <section className="bg-[#111827] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg shadow-black/30">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 font-black text-xs">
                  3
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-bold text-white">Forma de Pagamento</h2>
                  <p className="text-[11px] text-gray-400">Pague online via Pix ou na entrega do pedido</p>
                </div>
              </div>

              {/* LISTA DE MÉTODOS DE PAGAMENTO - COLUNA ÚNICA AMPLA E CONFORTÁVEL */}
              <div className="flex flex-col space-y-2.5 mb-4">
                
                {/* 1. DINHEIRO */}
                <div
                  onClick={() => setFormaPagamento('dinheiro')}
                  className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all active:scale-[0.99] flex items-center justify-between gap-3 ${
                    formaPagamento === 'dinheiro'
                      ? 'bg-orange-500/15 border-orange-500 ring-2 ring-orange-500/20 text-white shadow-md shadow-orange-500/10'
                      : 'bg-[#151c2c] border-white/5 text-gray-300 hover:border-white/15'
                  }`}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                      formaPagamento === 'dinheiro' ? 'bg-orange-500/20 text-orange-400' : 'bg-white/5 text-gray-400'
                    }`}>
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base font-bold text-white leading-tight">Dinheiro</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">Pagar em notas na entrega</p>
                    </div>
                  </div>

                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    formaPagamento === 'dinheiro' ? 'border-orange-500 bg-orange-500' : 'border-gray-600 bg-transparent'
                  }`}>
                    {formaPagamento === 'dinheiro' && <Check className="w-3 h-3 text-white stroke-[3]" />}
                  </div>
                </div>

                {/* 2. CARTÃO NA ENTREGA */}
                <div
                  onClick={() => setFormaPagamento('cartao_maquininha')}
                  className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all active:scale-[0.99] flex items-center justify-between gap-3 ${
                    formaPagamento === 'cartao_maquininha'
                      ? 'bg-orange-500/15 border-orange-500 ring-2 ring-orange-500/20 text-white shadow-md shadow-orange-500/10'
                      : 'bg-[#151c2c] border-white/5 text-gray-300 hover:border-white/15'
                  }`}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                      formaPagamento === 'cartao_maquininha' ? 'bg-orange-500/20 text-orange-400' : 'bg-white/5 text-gray-400'
                    }`}>
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base font-bold text-white leading-tight">Cartão na Maquininha</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">Débito ou Crédito na entrega</p>
                    </div>
                  </div>

                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    formaPagamento === 'cartao_maquininha' ? 'border-orange-500 bg-orange-500' : 'border-gray-600 bg-transparent'
                  }`}>
                    {formaPagamento === 'cartao_maquininha' && <Check className="w-3 h-3 text-white stroke-[3]" />}
                  </div>
                </div>

                {/* 3. PIX NA ENTREGA */}
                <div
                  onClick={() => setFormaPagamento('pix_maquininha')}
                  className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all active:scale-[0.99] flex items-center justify-between gap-3 ${
                    formaPagamento === 'pix_maquininha'
                      ? 'bg-orange-500/15 border-orange-500 ring-2 ring-orange-500/20 text-white shadow-md shadow-orange-500/10'
                      : 'bg-[#151c2c] border-white/5 text-gray-300 hover:border-white/15'
                  }`}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                      formaPagamento === 'pix_maquininha' ? 'bg-orange-500/20 text-orange-400' : 'bg-white/5 text-gray-400'
                    }`}>
                      <QrCode className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base font-bold text-white leading-tight">Pix na Entrega</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">QR Code na maquininha do entregador</p>
                    </div>
                  </div>

                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    formaPagamento === 'pix_maquininha' ? 'border-orange-500 bg-orange-500' : 'border-gray-600 bg-transparent'
                  }`}>
                    {formaPagamento === 'pix_maquininha' && <Check className="w-3 h-3 text-white stroke-[3]" />}
                  </div>
                </div>

                {/* 4. PIX ONLINE (MERCADO PAGO) - TÍTULO EM 1 LINHA E TAG INLINE */}
                <div
                  onClick={() => setFormaPagamento('pix_online')}
                  className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all active:scale-[0.99] flex items-center justify-between gap-3 ${
                    formaPagamento === 'pix_online'
                      ? 'bg-orange-500/15 border-orange-500 ring-2 ring-orange-500/20 text-white shadow-md shadow-orange-500/10'
                      : 'bg-[#151c2c] border-white/5 text-gray-300 hover:border-white/15'
                  }`}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                      formaPagamento === 'pix_online' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-gray-400'
                    }`}>
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm sm:text-base font-bold text-white whitespace-nowrap">
                          Pix Online no Site
                        </p>
                        <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0">
                          Instantâneo
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        Pague agora via QR Code na tela
                      </p>
                    </div>
                  </div>

                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    formaPagamento === 'pix_online' ? 'border-orange-500 bg-orange-500' : 'border-gray-600 bg-transparent'
                  }`}>
                    {formaPagamento === 'pix_online' && <Check className="w-3 h-3 text-white stroke-[3]" />}
                  </div>
                </div>
              </div>

              {/* OPÇÃO DE TROCO INTELIGENTE SE DINHEIRO */}
              {formaPagamento === 'dinheiro' && (
                <div className="p-4 rounded-2xl bg-[#151c2c] border border-white/10 space-y-3.5 animate-fade-in">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <Coins className="w-5 h-5 text-orange-400" />
                      <div>
                        <span className="text-xs sm:text-sm font-bold text-white block">Precisa de troco?</span>
                        <span className="text-[11px] text-gray-400">Informe se vai pagar com valor maior</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setPrecisaTroco(false)}
                        className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all active:scale-95 ${
                          !precisaTroco
                            ? 'bg-white/10 text-white border-white/20 shadow-sm'
                            : 'bg-[#1e2738] text-gray-400 border-white/5 hover:border-white/10 hover:text-gray-300'
                        }`}
                      >
                        Não
                      </button>
                      <button
                        type="button"
                        onClick={() => setPrecisaTroco(true)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all active:scale-95 flex items-center gap-1.5 ${
                          precisaTroco
                            ? 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20'
                            : 'bg-[#1e2738] text-gray-400 border-white/5 hover:border-white/10 hover:text-gray-300'
                        }`}
                      >
                        {precisaTroco && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                        <span>Sim, preciso</span>
                      </button>
                    </div>
                  </div>

                  {precisaTroco && (
                    <div className="space-y-3 pt-3 border-t border-white/10 animate-fade-in">
                      <div>
                        <span className="text-xs text-gray-300 font-semibold block mb-2">
                          Atalhos rápidos de notas:
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {[20, 50, 100, 200].map(val => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => setValorTrocoPara(val.toString())}
                              className={`px-3 py-2 rounded-xl text-xs font-black border transition-all active:scale-95 ${
                                valorTrocoPara === val.toString()
                                  ? 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20'
                                  : 'bg-[#1e2738] text-gray-300 border-white/10 hover:border-white/20'
                              }`}
                            >
                              R$ {val}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-300 shrink-0">Troco para: R$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Ex: 50,00"
                          value={valorTrocoPara}
                          onChange={(e) => setValorTrocoPara(e.target.value)}
                          className="flex-1 bg-[#1e2738] border border-white/10 rounded-xl px-3.5 py-2.5 text-base text-white placeholder-gray-500 font-bold focus:outline-none focus:border-orange-500"
                        />
                      </div>

                      {trocoCalculado > 0 ? (
                        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-xs font-bold text-emerald-400">
                          <span>Seu troco será de:</span>
                          <span className="text-sm font-black">R$ {trocoCalculado.toFixed(2)}</span>
                        </div>
                      ) : valorTrocoPara && parseFloat(valorTrocoPara.replace(',', '.')) <= valorTotalFinal ? (
                        <p className="text-xs text-amber-400">
                          O valor deve ser maior que o total do pedido (R$ {valorTotalFinal.toFixed(2)})
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* CARD 4: OBSERVAÇÕES E CUPOM COM ALTO CONTRASTE */}
            <section className="bg-[#111827] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg shadow-black/30 space-y-4">
              <div>
                <label className="block text-xs font-bold text-white mb-2">
                  Cupom de Desconto
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="CUPOM DE DESCONTO"
                      value={codigoCupom}
                      onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                      className="w-full bg-[#151c2c] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-base text-white uppercase placeholder-gray-500 font-bold focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAplicarCupom}
                    disabled={validandoCupom || !codigoCupom.trim()}
                    className="bg-gradient-to-r from-orange-500 to-amber-500 hover:brightness-110 disabled:opacity-40 text-white font-black px-5 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md shadow-orange-500/20 active:scale-95 transition-all shrink-0"
                  >
                    {validandoCupom ? '...' : 'Aplicar'}
                  </button>
                </div>

                {cupomAplicado && (
                  <div className="mt-2.5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-xs font-bold text-emerald-400 animate-fade-in">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      <span>Cupom {cupomAplicado.codigo} ativado</span>
                    </div>
                    <span>- R$ {cupomAplicado.desconto.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-white mb-2">
                  Observações para a Cozinha ou Entregador
                </label>
                <textarea
                  rows={2}
                  placeholder="Ex: Ponto da carne, sem campainha, chamar no portão..."
                  value={observacoesPedido}
                  onChange={(e) => setObservacoesPedido(e.target.value)}
                  className="w-full bg-[#151c2c] border border-white/10 rounded-xl p-3.5 text-base sm:text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-none transition-all"
                />
              </div>
            </section>
          </div>

          {/* ════════════ COLUNA DIREITA: RESUMO (DESKTOP) ════════════ */}
          <div className="hidden lg:block lg:col-span-5 sticky top-20 space-y-4">
            <div className="bg-[#111827] border border-white/10 rounded-3xl p-5 shadow-xl shadow-black/40">
              <div className="flex items-center justify-between border-b border-white/5 pb-3.5 mb-3.5">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-orange-400" />
                  Resumo do Pedido
                </h3>
                <span className="text-xs text-gray-400 font-bold">
                  {cart.itemCount} {cart.itemCount === 1 ? 'item' : 'itens'}
                </span>
              </div>

              {/* LISTA DE ITENS */}
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-4">
                {cart.cartData.itens.map((item) => (
                  <div key={item.id} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/5 flex items-start justify-between gap-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white truncate">{item.produto_nome}</p>
                      {item.personalizacoes?.length > 0 && (
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">
                          {item.personalizacoes.map((p: any) => p.nome).join(', ')}
                        </p>
                      )}
                      <p className="text-xs font-bold text-orange-400 mt-1">
                        R$ {(item.preco_unitario * item.quantidade).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 bg-[#151c2c] rounded-lg p-0.5 border border-white/10 shrink-0">
                      <button
                        type="button"
                        onClick={() => cart.updateItemQuantity(item.id, item.quantidade - 1)}
                        className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-white"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-bold w-4 text-center">{item.quantidade}</span>
                      <button
                        type="button"
                        onClick={() => cart.updateItemQuantity(item.id, item.quantidade + 1)}
                        className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-white"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* TOTAIS FINANCEIROS */}
              <div className="space-y-2 border-t border-white/5 pt-3.5 text-xs text-gray-400">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-semibold text-white">R$ {subtotalItens.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span>Entrega ({tipoEntrega === 'entrega' ? 'Domicílio' : 'Balcão'})</span>
                  <span className={`font-semibold ${valorFreteFinal === 0 ? 'text-emerald-400' : 'text-white'}`}>
                    {valorFreteFinal === 0 ? 'Grátis' : `R$ ${valorFreteFinal.toFixed(2)}`}
                  </span>
                </div>

                {cupomAplicado && (
                  <div className="flex justify-between text-emerald-400 font-semibold">
                    <span>Desconto do Cupom</span>
                    <span>- R$ {valorDescontoCupom.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-end text-sm font-black text-white pt-3 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-2xl text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                    R$ {valorTotalFinal.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* BOTÃO FINALIZAR (DESKTOP) */}
              <button
                type="button"
                onClick={handleFinalizarCompra}
                disabled={submitting}
                className="w-full mt-5 bg-gradient-to-r from-emerald-500 to-green-600 hover:brightness-110 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-98 transition-all disabled:opacity-50 text-xs uppercase tracking-wider"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Confirmar e Fazer Pedido</span>
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </main>

      {/* ── BARRA FIXA FLUTUANTE INFERIOR (MOBILE - EXCLUSIVO SMARTPHONES) ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1424]/95 backdrop-blur-2xl border-t border-white/10 p-3 sm:p-4 lg:hidden shadow-2xl">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Total</span>
            <span className="text-lg sm:text-xl font-black text-white">R$ {valorTotalFinal.toFixed(2)}</span>
          </div>

          <button
            type="button"
            onClick={handleFinalizarCompra}
            disabled={submitting}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:brightness-110 text-white font-black py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-98 transition-all disabled:opacity-50 text-xs uppercase tracking-wider"
          >
            {submitting ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            <span>Confirmar Pedido</span>
          </button>
        </div>
      </div>

      {/* ── MODAL DE LOGIN RÁPIDO DENTRO DO CHECKOUT ─────────────────── */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-[#111827] border border-white/10 rounded-2xl p-5 w-full max-w-sm shadow-2xl">
            <h3 className="text-base font-black text-white mb-1">Entrar na Conta</h3>
            <p className="text-xs text-gray-400 mb-4">
              Entre para puxar seus endereços salvos automaticamente.
            </p>

            <form onSubmit={handleLoginRapido} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">E-mail</label>
                <input
                  type="email"
                  placeholder="seu@email.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full bg-[#151c2c] border border-white/10 rounded-xl px-3 py-2.5 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Senha</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={loginSenha}
                  onChange={(e) => setLoginSenha(e.target.value)}
                  className="w-full bg-[#151c2c] border border-white/10 rounded-xl px-3 py-2.5 text-base text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                />
              </div>

              <div className="flex items-center gap-2 pt-1.5">
                <button
                  type="button"
                  onClick={() => setShowLoginModal(false)}
                  className="flex-1 bg-white/5 hover:bg-white/10 text-gray-300 font-bold py-2.5 rounded-xl text-xs transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl text-xs transition-colors"
                >
                  {loginLoading ? 'Entrando...' : 'Entrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL DO MERCADO PAGO (SE PIX ONLINE) ────────────────────── */}
      {showMercadoPago && pedidoCriado && (
        <MercadoPagoCheckout
          isOpen={showMercadoPago}
          onClose={() => setShowMercadoPago(false)}
          pedido={pedidoCriado}
          onPagamentoConfirmado={handleMercadoPagoConfirmado}
          dadosCarrinho={{
            itens: pedidoCriado.itens,
            observacoes: pedidoCriado.observacoes,
            valorFrete: pedidoCriado.valor_frete,
            enderecoEntrega: pedidoCriado.dados_cliente?.endereco,
            usuario: usuario || {
              id: 0,
              nome: nomeCliente,
              email: emailCliente || `${whatsappCliente.replace(/\D/g, '')}@delivery.com`,
              telefone: whatsappCliente
            }
          }}
        />
      )}
    </div>
  );
};

export default CheckoutPage;
